/**
 * PWA Preload Splash Screen Component
 * Shows loading progress while chunks are being downloaded
 * For LG WebOS and Samsung Tizen Smart TVs
 */

import React, { useState, useEffect } from 'react';

const PreloadSplash = ({ children, onComplete }) => {
    const [progress, setProgress] = useState(0);
    const [loaded, setLoaded] = useState(0);
    const [total, setTotal] = useState(0);
    const [showContent, setShowContent] = useState(false);
    const [isOffline, setIsOffline] = useState(!navigator.onLine);

    useEffect(() => {
        var finished = false;
        var hasProgress = false;
        var completionTimer = null;
        var timeout = null;
        var minDelay = null;

        var showApplication = function () {
            if (finished) return;
            finished = true;
            clearTimeout(timeout);
            clearTimeout(minDelay);
            clearTimeout(completionTimer);
            setShowContent(true);
            if (onComplete) onComplete();
        };

        // Listen for preload progress
        const handleProgress = (event) => {
            const { loaded: l, total: t, progress: p } = event.detail;
            hasProgress = p > 0;
            setLoaded(l);
            setTotal(t);
            setProgress(p);
        };

        // Listen for preload complete
        const handleComplete = (event) => {
            console.log('[PreloadSplash] Preload complete:', event.detail);
            setProgress(100);

            // Delay to show 100% for a moment
            clearTimeout(completionTimer);
            completionTimer = setTimeout(showApplication, 500);
        };

        // Listen for network status
        const handleNetworkStatus = (event) => {
            setIsOffline(!event.detail.online);
        };

        window.addEventListener('pwa-preload-progress', handleProgress);
        window.addEventListener('pwa-preload-complete', handleComplete);
        window.addEventListener('pwa-network-status', handleNetworkStatus);

        // Auto-complete after timeout (in case preload doesn't trigger)
        timeout = setTimeout(() => {
            console.log('[PreloadSplash] Timeout - showing content');
            showApplication();
        }, 8000); // 8 second maximum wait

        // Show content after a minimum delay (for branding)
        minDelay = setTimeout(() => {
            if (!hasProgress) {
                // No chunks being loaded, just show content
                showApplication();
            }
        }, 2000);

        return () => {
            window.removeEventListener('pwa-preload-progress', handleProgress);
            window.removeEventListener('pwa-preload-complete', handleComplete);
            window.removeEventListener('pwa-network-status', handleNetworkStatus);
            clearTimeout(timeout);
            clearTimeout(minDelay);
            clearTimeout(completionTimer);
        };
    }, [onComplete]);

    // Show content if already loaded or after animation
    if (showContent) {
        return children;
    }

    return (
        <div style={styles.container}>
            <div style={styles.content}>
                {/* Logo */}
                <div style={styles.logoContainer}>
                    <img
                        src="/logo192.png"
                        alt="منه باخ"
                        style={styles.logo}
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                </div>

                {/* App Name */}
                <h1 style={styles.title}>منه باخ</h1>

                {/* Loading indicator */}
                <div style={styles.progressContainer}>
                    <div style={styles.progressTrack}>
                        <div
                            style={{
                                ...styles.progressFill,
                                width: `${progress}%`,
                            }}
                        />
                    </div>
                    <div style={styles.progressText}>
                        {isOffline ? (
                            <span style={styles.offlineText}>آفلاین - در حال بارگذاری از کش...</span>
                        ) : progress > 0 ? (
                            <span>در حال بارگذاری... {progress}%</span>
                        ) : (
                            <span>در حال آماده‌سازی...</span>
                        )}
                    </div>
                </div>

                {/* Chunks loaded info */}
                {total > 0 && (
                    <div style={styles.chunkInfo}>
                        {loaded} از {total} فایل بارگذاری شد
                    </div>
                )}
            </div>

            {/* Version info */}
            <div style={styles.version}>
                نسخه 1.5
            </div>

            {/* Inline CSS for animations */}
            <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        @keyframes slideIn {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
        </div>
    );
};

const styles = {
    container: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(180deg, #1a0505 0%, #0a0202 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: '"AnjomanMax", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        direction: 'rtl',
        zIndex: 9999,
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        animation: 'slideIn 0.5s ease-out',
    },
    logoContainer: {
        width: 120,
        height: 120,
        marginBottom: 24,
        animation: 'pulse 2s ease-in-out infinite',
    },
    logo: {
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        borderRadius: 24,
    },
    title: {
        fontSize: 48,
        fontWeight: 700,
        color: '#ffffff',
        margin: 0,
        marginBottom: 8,
        textShadow: '0 2px 10px rgba(0,0,0,0.5)',
    },
    subtitle: {
        fontSize: 18,
        color: 'rgba(255, 255, 255, 0.7)',
        margin: 0,
        marginBottom: 48,
    },
    progressContainer: {
        width: 300,
        textAlign: 'center',
    },
    progressTrack: {
        width: '100%',
        height: 6,
        background: 'rgba(255, 255, 255, 0.1)',
        borderRadius: 3,
        overflow: 'hidden',
    },
    progressFill: {
        height: '100%',
        background: 'linear-gradient(90deg, #850000 0%, #c50000 100%)',
        borderRadius: 3,
        transition: 'width 0.3s ease-out',
    },
    progressText: {
        marginTop: 16,
        fontSize: 14,
        color: 'rgba(255, 255, 255, 0.6)',
    },
    offlineText: {
        color: '#ffc107',
    },
    chunkInfo: {
        marginTop: 8,
        fontSize: 12,
        color: 'rgba(255, 255, 255, 0.4)',
    },
    version: {
        position: 'absolute',
        bottom: 24,
        fontSize: 12,
        color: 'rgba(255, 255, 255, 0.3)',
    },
};

export default PreloadSplash;
